import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Button, IconButton, Input } from '@/components';
import { computeAnchoredPanelPosition } from '@/lib/anchored-panel';
import { cn } from '@/lib/utils';

import { EXTENDED_COLORS, PRESET_COLORS } from '../styleConstants';

import { RangeSlider } from './RangeSlider';

/** Szerokość popovera (px) — stała, niezależna od szerokości triggera (swatch 32px). */
const POPOVER_WIDTH = 256;

interface PopoverPosition {
  top: number;
  left: number;
  maxHeight: number;
}

/**
 * Pozycja popovera koloru — pionowo przez `computeAnchoredPanelPosition` (odbicie nad/pod
 * triggerem), poziomo z własnym przycięciem do okna: swatch-trigger bywa tuż przy prawej
 * krawędzi wąskiego panelu właściwości, więc samo `left = rect.left` wypychałoby 256px
 * szeroki popover poza viewport.
 */
function computeColorPopoverPosition(rect: DOMRect): PopoverPosition {
  // `width: POPOVER_WIDTH` (nie `rect.width` triggera) — helper zwraca `left`/`width`
  // 1:1 z prostokątem, który dostaje, więc podajemy mu od razu docelową szerokość popovera.
  const base = computeAnchoredPanelPosition(
    { top: rect.top, bottom: rect.bottom, left: rect.left, width: POPOVER_WIDTH },
    { preferredMaxHeight: 420 },
  );
  const margin = 8;
  const maxLeft = window.innerWidth - POPOVER_WIDTH - margin;
  const left = Math.min(rect.left, Math.max(margin, maxLeft));
  return { top: base.top, left, maxHeight: base.maxHeight };
}

interface ColorPickerAdvancedProps {
  color: string | undefined;
  onChange: (color: string | undefined) => void;
  opacity?: number;
  onOpacityChange?: (opacity: number) => void;
  label?: string;
  allowClear?: boolean;
  /**
   * Tylko swatch, bez pola hex i przycisku czyszczenia — dla ciasnych wierszy
   * (np. punkt koloru gradientu, gdzie w jednej linii jest jeszcze % i kosz).
   * Edycja hex nadal dostępna w popoverze po kliknięciu.
   */
  compact?: boolean;
}

function hexToHSV(hex: string): { h: number; s: number; v: number } {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return { h: h * 360, s: max === 0 ? 0 : d / max, v: max };
}

function hsvToHex(h: number, s: number, v: number): string {
  const hh = h / 60;
  const c = v * s;
  const x = c * (1 - Math.abs((hh % 2) - 1));
  const m = v - c;
  let r = 0,
    g = 0,
    b = 0;
  if (hh >= 0 && hh < 1) {
    r = c;
    g = x;
  } else if (hh < 2) {
    r = x;
    g = c;
  } else if (hh < 3) {
    g = c;
    b = x;
  } else if (hh < 4) {
    g = x;
    b = c;
  } else if (hh < 5) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }
  const toHex = (n: number) =>
    Math.round((n + m) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function ColorPickerAdvanced({
  color,
  onChange,
  opacity,
  onOpacityChange,
  label,
  allowClear = true,
  compact = false,
}: ColorPickerAdvancedProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [hexInput, setHexInput] = useState(color || '#000000');
  const [hsv, setHsv] = useState(() => hexToHSV(color || '#3B82F6'));
  const [showExtended, setShowExtended] = useState(false);
  const [popoverPos, setPopoverPos] = useState<PopoverPosition | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const satPadRef = useRef<HTMLDivElement>(null);
  const huePadRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (color) {
      setHexInput(color);
      setHsv(hexToHSV(color));
    }
  }, [color]);

  // Popover w PORTALU (poza triggerRef) — inaczej `position:absolute` przycina rodzic
  // z overflow-hidden (sekcja akordeonu w StylePanelAdvanced, §4.12).
  useEffect(() => {
    if (!isOpen) {
      setPopoverPos(null);
      return;
    }
    const update = () => {
      const el = triggerRef.current;
      if (!el) return;
      setPopoverPos(computeColorPopoverPosition(el.getBoundingClientRect()));
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const inTrigger = triggerRef.current?.contains(target) ?? false;
      const inPopover = popoverRef.current?.contains(target) ?? false;
      if (!inTrigger && !inPopover) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const updateFromHSV = useCallback(
    (h: number, s: number, v: number) => {
      const newHex = hsvToHex(h, s, v);
      setHsv({ h, s, v });
      setHexInput(newHex);
      onChange(newHex);
    },
    [onChange],
  );

  const handleSatPadMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    const update = (clientX: number, clientY: number) => {
      const rect = satPadRef.current!.getBoundingClientRect();
      const s = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const v = Math.max(0, Math.min(1, 1 - (clientY - rect.top) / rect.height));
      updateFromHSV(hsv.h, s, v);
    };
    update(e.clientX, e.clientY);
    const onMove = (me: MouseEvent) => update(me.clientX, me.clientY);
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const handleHueMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    const update = (clientX: number) => {
      const rect = huePadRef.current!.getBoundingClientRect();
      const h = Math.max(0, Math.min(360, ((clientX - rect.left) / rect.width) * 360));
      updateFromHSV(h, hsv.s, hsv.v);
    };
    update(e.clientX);
    const onMove = (me: MouseEvent) => update(me.clientX);
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const handleHexSubmit = () => {
    let hex = hexInput.trim();
    if (!hex.startsWith('#')) hex = '#' + hex;
    if (/^#[0-9a-fA-F]{6}$/.test(hex)) {
      onChange(hex);
      setHsv(hexToHSV(hex));
    }
  };

  return (
    <div className="relative" ref={triggerRef}>
      {label && (
        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
          {label}
        </label>
      )}
      <div className="flex items-center gap-2 min-w-0">
        <Button
          type="button"
          variant="ghost"
          onClick={() => setIsOpen(!isOpen)}
          aria-label={t('tenant.systemModuleBuilder.style.common.color')}
          className="h-8 w-8 flex-shrink-0 rounded-lg border-2 border-gray-300 p-0 shadow-sm hover:border-gray-400 dark:border-gray-600"
          style={{ backgroundColor: color || 'transparent' }}
        />
        {!compact && (
          <Input
            type="text"
            value={hexInput}
            onChange={(e) => {
              const val = e.target.value;
              setHexInput(val);
              // Commit na żywo, gdy wartość jest już poprawnym #RRGGBB (parytet z rozmiarem/suwakiem).
              // Krótsze/niepełne wpisy commituje dopiero blur/Enter (handleHexSubmit).
              let hex = val.trim();
              if (!hex.startsWith('#')) hex = '#' + hex;
              if (/^#[0-9a-fA-F]{6}$/.test(hex)) {
                onChange(hex);
                setHsv(hexToHSV(hex));
              }
            }}
            onBlur={handleHexSubmit}
            onKeyDown={(e) => e.key === 'Enter' && handleHexSubmit()}
            containerClassName="min-w-0 flex-1"
            className="px-2 py-1.5 font-mono text-xs"
            placeholder="#000000"
          />
        )}
        {!compact && allowClear && color && (
          <IconButton
            variant="ghost"
            size="sm"
            aria-label={t('tenant.systemModuleBuilder.style.common.clear')}
            onClick={() => {
              onChange(undefined);
              setHexInput('');
            }}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <span aria-hidden="true" className="text-xs">
              ×
            </span>
          </IconButton>
        )}
      </div>

      {isOpen &&
        popoverPos &&
        createPortal(
          <div
            ref={popoverRef}
            style={{
              position: 'fixed',
              top: popoverPos.top,
              left: popoverPos.left,
              width: POPOVER_WIDTH,
              maxHeight: popoverPos.maxHeight,
              zIndex: 60,
            }}
            className="overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-3 space-y-3"
          >
            {/* Saturation/Value Pad */}
            <div
              ref={satPadRef}
              className="w-full h-36 rounded-lg cursor-crosshair relative overflow-hidden"
              style={{ backgroundColor: hsvToHex(hsv.h, 1, 1) }}
              onMouseDown={handleSatPadMouseDown}
            >
              <div
                className="absolute inset-0"
                style={{ background: 'linear-gradient(to right, #fff, transparent)' }}
              />
              <div
                className="absolute inset-0"
                style={{ background: 'linear-gradient(to top, #000, transparent)' }}
              />
              <div
                className="absolute w-4 h-4 border-2 border-white rounded-full shadow-md -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                style={{
                  left: `${hsv.s * 100}%`,
                  top: `${(1 - hsv.v) * 100}%`,
                  backgroundColor: color || '#000',
                }}
              />
            </div>

            {/* Hue Bar */}
            <div
              ref={huePadRef}
              className="w-full h-3 rounded-full cursor-pointer relative"
              style={{
                background:
                  'linear-gradient(to right, #f00 0%, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00 100%)',
              }}
              onMouseDown={handleHueMouseDown}
            >
              <div
                className="absolute w-4 h-4 border-2 border-white rounded-full shadow-md -translate-x-1/2 top-1/2 -translate-y-1/2 pointer-events-none"
                style={{
                  left: `${(hsv.h / 360) * 100}%`,
                  backgroundColor: hsvToHex(hsv.h, 1, 1),
                }}
              />
            </div>

            {/* Opacity Bar */}
            {onOpacityChange && (
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-gray-400 w-6">α</span>
                <RangeSlider
                  min={0}
                  max={100}
                  value={opacity ?? 100}
                  onChange={(v) => onOpacityChange(Math.round(v))}
                  className="flex-1"
                  aria-label={t('tenant.systemModuleBuilder.style.common.opacity')}
                />
                <span className="text-[10px] text-gray-400 w-8 text-right">{opacity ?? 100}%</span>
              </div>
            )}

            {/* Swatches */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] text-gray-400 uppercase tracking-wider">
                  {t('tenant.systemModuleBuilder.style.color.swatches')}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowExtended(!showExtended)}
                  className="px-1 py-0 text-[10px] text-accent-500 hover:bg-transparent hover:text-accent-600"
                >
                  {showExtended
                    ? t('tenant.systemModuleBuilder.style.color.less')
                    : t('tenant.systemModuleBuilder.style.color.more')}
                </Button>
              </div>
              <div className="grid grid-cols-7 gap-1">
                {(showExtended ? EXTENDED_COLORS : PRESET_COLORS).map((c) => (
                  <Button
                    key={c}
                    type="button"
                    variant="ghost"
                    aria-label={c}
                    onClick={() => {
                      onChange(c);
                      setHexInput(c);
                      setHsv(hexToHSV(c));
                    }}
                    className={cn(
                      'aspect-square w-full rounded border p-0 transition-transform hover:scale-110',
                      color === c
                        ? 'border-accent-500 ring-1 ring-accent-500 scale-110'
                        : 'border-gray-200 dark:border-gray-600',
                    )}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
